import { motion } from 'framer-motion'
import type { FulfillmentContent } from '../../data/fulfillment'

/**
 * Mock du dashboard client Yaslan — vue « Analytiques » (fidèle au SaaS réel) :
 * volume d'expéditions, taux de retour, top transporteurs, taux de livraison.
 * Thème clair, chrome navigateur, animations déclenchées au scroll.
 */

// Données déterministes (allure réaliste, stables entre rendus SSR/client).
const VOLUME_BARS = [2, 6, 7, 0, 3, 8, 2, 3, 11, 4, 3, 8, 2, 5, 6, 6, 6, 3, 4, 7, 4, 4, 5, 4, 3, 2]
const TOP_CARRIERS = [
  { name: 'bpost', pct: 92 },
  { name: 'GLS', pct: 78 },
  { name: 'DPD', pct: 38 },
  { name: 'DHL', pct: 36 },
  { name: 'PostNL', pct: 28 },
]
const DELIVERY_RATES = [
  { name: 'GLS', pct: 99 },
  { name: 'PostNL', pct: 97 },
  { name: 'DSV', pct: 96 },
  { name: 'DPD', pct: 95 },
  { name: 'DHL', pct: 95 },
]

const EASE = [0.22, 1, 0.36, 1] as const

function ExportButton({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-1.5 py-0.5 text-[8px] font-medium text-ink-600">
      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <path d="M12 3v12M7 10l5 5 5-5M4 21h16" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label}
    </span>
  )
}

type Props = {
  mock: FulfillmentContent['mock']
}

export default function AnalyticsMockup({ mock }: Props) {
  return (
    <div className="relative mx-auto w-full">
      {/* Halo ambiant */}
      <div className="absolute -inset-6 -z-10 rounded-[32px] bg-gradient-to-b from-accent-500/25 via-accent-500/10 to-transparent blur-3xl" />

      <motion.div
        initial={{ y: 24, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative rounded-2xl bg-white/80 backdrop-blur-xl border border-ink-200 shadow-[0_40px_120px_-30px_rgba(14,14,20,0.35),0_20px_60px_-20px_rgba(59,93,255,0.25)] overflow-hidden"
      >
        {/* Barre de titre navigateur */}
        <div className="flex items-center gap-3 px-4 h-10 border-b border-ink-100 bg-white/70">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-rose-400/80" />
            <span className="w-3 h-3 rounded-full bg-amber-400/80" />
            <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
          </div>
          <div className="mx-auto flex items-center gap-2 text-xs text-ink-500 bg-ink-50 border border-ink-100 rounded-md px-3 py-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            app.yaslan.be
          </div>
        </div>

        {/* Écran */}
        <div className="relative bg-[#fafbfc] p-3 sm:p-5">
          {/* En-tête : titre + périodes */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="flex flex-wrap items-center justify-between gap-2"
          >
            <div>
              <h4 className="text-[13px] sm:text-sm font-semibold text-ink-950 tracking-tight">
                {mock.title}
              </h4>
              <p className="text-[9px] sm:text-[10px] text-ink-500">
                {mock.subtitle} · {mock.periods[1]}
              </p>
            </div>
            <div className="flex items-center rounded-lg border border-ink-100 bg-white p-0.5">
              {mock.periods.map((p, i) => (
                <span
                  key={p}
                  className={`px-2 py-1 rounded-md text-[8px] sm:text-[9px] font-medium ${
                    i === 1 ? 'bg-ink-950 text-white' : 'text-ink-500'
                  }`}
                >
                  {p}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Volume d'expéditions */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mt-3 rounded-xl border border-ink-100 bg-white p-3 sm:p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[10px] sm:text-[11px] font-semibold text-ink-900">
                  {mock.volumeTitle}
                </p>
                <p className="text-[8px] sm:text-[9px] text-ink-500">{mock.volumeSub}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="hidden sm:inline-flex items-center gap-1 text-[8px] text-accent-700 bg-accent-50 border border-accent-100 rounded-md px-1.5 py-0.5">
                  <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" />
                  {mock.live}
                </span>
                <ExportButton label={mock.exportCsv} />
              </div>
            </div>
            <div className="mt-3 flex items-end gap-[3px] h-20 sm:h-24">
              {VOLUME_BARS.map((v, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${Math.max(4, (v / 11) * 100)}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.35 + i * 0.025, duration: 0.5, ease: EASE }}
                  className="flex-1 rounded-t-[3px] bg-gradient-to-t from-accent-600 to-accent-400 min-w-0"
                  style={{ opacity: v === 0 ? 0.15 : 1 }}
                />
              ))}
            </div>
          </motion.div>

          {/* Rangée : taux de retour + transporteurs */}
          <div className="mt-3 grid grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Taux de retour */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="rounded-xl border border-ink-100 bg-white p-3 sm:p-4"
            >
              <p className="text-[10px] sm:text-[11px] font-semibold text-ink-900">
                {mock.returnsTitle}
              </p>
              <p className="text-[8px] sm:text-[9px] text-ink-500">{mock.returnsSub}</p>
              <svg viewBox="0 0 100 40" className="mt-2 w-full h-14 sm:h-16" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="returnsFill" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <motion.path
                  d="M0 38 L8 36 Q12 26 16 36 L26 37 Q32 14 38 37 L48 38 Q53 28 58 38 L66 37 Q71 22 76 37 L86 38 Q91 30 96 38 L100 38 L100 40 L0 40 Z"
                  fill="url(#returnsFill)"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.8 }}
                />
                <motion.path
                  d="M0 38 L8 36 Q12 26 16 36 L26 37 Q32 14 38 37 L48 38 Q53 28 58 38 L66 37 Q71 22 76 37 L86 38 Q91 30 96 38 L100 38"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.3, ease: EASE, delay: 0.55 }}
                />
              </svg>
            </motion.div>

            {/* Top transporteurs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="rounded-xl border border-ink-100 bg-white p-3 sm:p-4"
            >
              <p className="text-[10px] sm:text-[11px] font-semibold text-ink-900">
                {mock.carriersTitle}
              </p>
              <p className="text-[8px] sm:text-[9px] text-ink-500">{mock.carriersSub}</p>
              <div className="mt-2.5 space-y-1.5">
                {TOP_CARRIERS.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span className="text-[8px] sm:text-[9px] text-ink-700 w-12 truncate">{c.name}</span>
                    <div className="flex-1 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${c.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.65 + i * 0.08, duration: 0.7, ease: EASE }}
                        className="h-full bg-gradient-to-r from-accent-500 to-accent-600"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Taux de livraison */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="hidden lg:block rounded-xl border border-ink-100 bg-white p-3 sm:p-4"
            >
              <p className="text-[10px] sm:text-[11px] font-semibold text-ink-900">
                {mock.deliveryTitle}
              </p>
              <p className="text-[8px] sm:text-[9px] text-ink-500">{mock.deliverySub}</p>
              <div className="mt-2.5 space-y-1.5">
                {DELIVERY_RATES.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span className="text-[8px] sm:text-[9px] text-ink-700 w-12 truncate">{c.name}</span>
                    <div className="flex-1 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${c.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.75 + i * 0.08, duration: 0.7, ease: EASE }}
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                      />
                    </div>
                    <span className="text-[8px] text-ink-500 tabular-nums w-6 text-right">{c.pct}%</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
